import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Every directory you actually run',
    description:
      'AD, Entra ID, OpenLDAP, OUD, Workday, BambooHR, Google Workspace, Okta, Microsoft 365: Monosync connects in both directions.',
    bullets: [
      'Active Directory and Entra ID',
      'OpenLDAP, Oracle Unified Directory',
      'Workday and BambooHR (HRIS)',
      'Google Workspace and Microsoft 365',
    ],
  },
  {
    title: 'Bidirectional, conflict-aware',
    description:
      'Push, pull, or bidirectional sync per source. When attributes diverge, named precedence rules pick the winner; no surprises.',
    bullets: [
      'Per-source direction (read, write, both)',
      'Configurable precedence on conflict',
      'Delta-based replication, not full sweeps',
      'Replay log for audit and rollback',
    ],
  },
  {
    title: 'Custom connectors via REST',
    description:
      'In-house apps and obscure SaaS still need to be in the picture. The custom REST connector turns any HTTP API into a sync target.',
    bullets: [
      'REST connector with auth and pagination',
      'Custom attribute mapping per endpoint',
      'Webhook triggers for inbound events',
      'SDK for advanced integration scenarios',
    ],
  },
  {
    title: 'Operational observability',
    description:
      'Every connector has a health dashboard. Failures retry; persistent issues page the owner. No more "is the sync running?"',
    bullets: [
      'Per-connector health and latency metrics',
      'Configurable retry and backoff policy',
      'Failure alerts to PagerDuty / Slack / email',
      'Run history searchable by user and event',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Pick the connector',
    description:
      'Choose from the catalog or build a custom REST integration. Map attributes once.',
  },
  {
    title: 'Set direction and policy',
    description:
      'Decide read-only, write-only, or bidirectional. Define precedence rules and retry policy.',
  },
  {
    title: 'Watch it run',
    description:
      'Health metrics, alerts, and replayable logs make sync feel like a deployed service, not a cron job.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Monosync API',
    description:
      'REST API for object create / update on the unified identity graph.',
    href: '/sync/api',
  },
  {
    label: 'Monosync Governance',
    description:
      'Entitlement mapping and access review on top of sync.',
    href: '/sync/governance',
  },
  {
    label: 'Directory & sources',
    description:
      'The Monosign side of the same directory connectivity.',
    href: '/sign/directory',
  },
]

export default function ConnectorsPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Monosync Connectors"
        description="Bidirectional sync between every directory, HRIS, and SaaS app you run. AD, Entra ID, LDAP, Workday, Google, Microsoft 365, and custom REST."
        path="/sync/connectors"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monosync · Connectors"
          title={
            <>
              One sync layer
              <br />
              for every directory.
            </>
          }
          body="Bidirectional, conflict-aware connectors for AD, LDAP, HRIS, and major SaaS, plus a custom REST connector for everything else. Delta sync, observable runs, replayable history."
          badges={[
            'Bidirectional, per-source',
            'Custom REST connector',
            'Health dashboards built in',
          ]}
        />
        <SolutionPillars
          title={<>Connectors that behave like services, not scripts.</>}
          body="Monosync connectors are deployed, monitored, and operated like first-class services. They retry, alert, log, and replay, so the sync layer never becomes a mystery."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>Pick. Direction. Observe.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
